import { IEvent, IEventInvestigationResult, IEventUpdate } from '@app/api/interfaces/event.interface';
import { isSamePosition } from '@app/main/main-content/map/utils/geometry';
import { LdEventInvestigationResult } from '@app/main/leak-detection/enums/ld-event-investigation-result.enum';
import { LdEventRepairStatus } from '@app/main/leak-detection/enums/ld-event-repair-status.enum';
import { IDialogEventDraft } from '@app/main/leak-detection/dialogs/dialog-event/interfaces/dialog-event-draft.interface';

/** what `draft` changes about `current`, as the api takes it — the way back of `toDraft` */
export function toEventUpdate(current: IEvent, draft: IDialogEventDraft): IEventUpdate {
  const data: IEventUpdate = {};

  if (draft.status !== current.status) data.status = draft.status;
  if ((draft.assignedTo ?? null) !== (current.assignedTo ?? null)) data.assignedTo = draft.assignedTo;
  if ((draft.location ?? null) !== (current.location ?? null)) data.location = draft.location;
  if (draft.hidden !== current.hidden) data.hidden = draft.hidden;

  if (draft.coordinates && !isSamePosition(draft.coordinates, current.coordinates)) {
    data.coordinates = draft.coordinates;
  }

  const outcome = toInvestigationResult(draft);

  if (!sameInvestigationResult(outcome, current.investigationResult)) data.investigationResult = outcome;

  return data;
}

function toInvestigationResult(draft: IDialogEventDraft): IEventUpdate['investigationResult'] {
  const { investigationResult } = draft;

  if (!investigationResult) return null;

  const base = { pressure: draft.pressure, soilComposition: draft.soilComposition };

  if (investigationResult === LdEventInvestigationResult.NoLeak) {
    return {
      ...base,
      status: investigationResult,
      nonLeakNoiseSource: draft.nonLeakNoiseSource,
      nonLeakOtherDetails: draft.nonLeakOtherDetails,
    };
  }

  const repairStatus = draft.repairStatus ?? LdEventRepairStatus.NotRepaired;

  return {
    ...base,
    status: investigationResult,
    leakSize: draft.leakSize,
    repairStatus,
    // `NotRepaired` is the absence of a date, scheduled or completed alike
    repairDate: repairStatus === LdEventRepairStatus.NotRepaired ? null : draft.repairDate,
  };
}

/** field by field, with a missing field and `null` the same — the api leaves out what the draft sends as `null` */
function sameInvestigationResult(
  a: IEventInvestigationResult | null | undefined,
  b: IEventInvestigationResult | null | undefined
): boolean {
  const left = (a ?? null) as Record<string, unknown> | null;
  const right = (b ?? null) as Record<string, unknown> | null;

  if (!left || !right) return left === right;

  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);

  return [...keys].every(key => (left[key] ?? null) === (right[key] ?? null));
}
